<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useSessionStore } from "../stores/session";
import {
  LayoutDashboard,
  GraduationCap,
  BadgeCheck,
  Router,
  ScrollText,
  ChartNoAxesCombined,
  Building2,
  Settings,
  LogOut,
  Menu,
  X,
  RefreshCw,
} from "@lucide/vue";
import UiSelect from "../ui/UiSelect.vue";

const session = useSessionStore();
const route = useRoute();
const router = useRouter();
const mobileOpen = ref(false);
const refreshing = ref(false);
const schoolOptions = computed(() => [
  { value: "", label: "Tüm okullar", description: "Kurumların tamamını göster" },
  ...session.schools.map((school) => ({
    value: String(school.id),
    label: school.name,
    description: school.code || "Erva Spor Okulu",
  })),
]);
const menu = computed(() =>
  [
    { to: "/", label: "Panel", icon: LayoutDashboard },
    { to: "/ogrenciler", label: "Öğrenciler", icon: GraduationCap },
    { to: "/kartlar", label: "Kartlar", icon: BadgeCheck },
    ...(!session.isParent
      ? [{ to: "/cihazlar", label: "Cihazlar", icon: Router }]
      : []),
    {
      to: "/raporlar",
      label: "Raporlar",
      icon: ChartNoAxesCombined,
      hidden: session.isParent,
    },
    { to: "/loglar", label: "Geçiş Logları", icon: ScrollText },
    {
      to: "/okullar",
      label: "Okullar",
      icon: Building2,
      hidden: !session.isSuper,
    },
    {
      to: "/ayarlar",
      label: "Ayarlar",
      icon: Settings,
      hidden: !session.isSuper,
    },
  ].filter((item) => !item.hidden),
);

function logout() {
  session.logout();
  router.replace("/giris");
}
async function refresh() {
  refreshing.value = true;
  await session.loadSchools().catch(() => {});
  window.dispatchEvent(new CustomEvent("lopiy:refresh"));
  setTimeout(() => {
    refreshing.value = false;
  }, 350);
}
</script>

<template>
  <div class="min-h-screen bg-slate-100">
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-50 flex w-[268px] flex-col bg-ink-900 text-white shadow-2xl transition-transform lg:translate-x-0',
        mobileOpen ? 'translate-x-0' : '-translate-x-full',
      ]"
    >
      <div class="flex h-20 items-center gap-3 border-b border-white/10 px-5">
        <div
          class="grid size-11 place-items-center rounded-lg bg-white shadow-sm"
        >
          <img
            src="/assets/valilik.png"
            class="size-9 object-contain"
            alt="Kayseri Valiligi"
          />
        </div>
        <div class="min-w-0">
          <strong class="block truncate text-sm">Kayseri Valiligi</strong
          ><span class="text-xs text-slate-400">Erva Spor Okullari</span>
        </div>
        <button
          class="ml-auto text-slate-400 lg:hidden"
          @click="mobileOpen = false"
        >
          <X class="size-5" />
        </button>
      </div>
      <nav class="flex-1 space-y-1 overflow-y-auto px-3 py-5">
        <p
          class="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500"
        >
          Yönetim
        </p>
        <RouterLink
          v-for="item in menu"
          :key="item.to"
          :to="item.to"
          @click="mobileOpen = false"
          :class="[
            'flex h-11 items-center gap-3 rounded-md px-3 text-sm font-semibold transition',
            route.path === item.to
              ? 'bg-white/10 text-white shadow-inner'
              : 'text-slate-300 hover:bg-white/5 hover:text-white',
          ]"
        >
          <component :is="item.icon" class="size-[18px]" /><span>{{
            item.label
          }}</span>
        </RouterLink>
      </nav>
      <div class="border-t border-white/10 p-4">
        <div class="flex items-center gap-3 px-1">
          <div
            class="grid size-9 place-items-center rounded-md bg-brand-600 text-xs font-extrabold"
          >
            {{ session.user?.full_name?.slice(0, 2)?.toUpperCase() || "LP" }}
          </div>
          <div class="min-w-0 flex-1">
            <strong class="block truncate text-xs">{{
              session.user?.full_name || session.user?.username
            }}</strong
            ><span class="text-[11px] text-slate-400">{{
              session.isSuper
                ? "Süper yönetici"
                : session.isParent
                  ? "Veli paneli"
                  : "Okul yöneticisi"
            }}</span>
          </div>
          <button
            class="text-slate-400 hover:text-white"
            title="Cikis yap"
            @click="logout"
          >
            <LogOut class="size-4" />
          </button>
        </div>
      </div>
    </aside>
    <div class="lg:pl-[268px]">
      <header
        class="sticky top-0 z-30 flex h-20 items-center border-b border-slate-200 bg-white/95 px-4 backdrop-blur lg:px-7"
      >
        <button class="icon-btn mr-3 lg:hidden" @click="mobileOpen = true">
          <Menu class="size-5" />
        </button>
        <div>
          <p class="page-kicker">Kayseri Valiligi</p>
          <h1 class="text-lg font-extrabold text-ink-950">
            {{ route.meta.title }}
          </h1>
        </div>
        <div class="ml-auto flex items-end gap-2">
          <label v-if="session.isSuper" class="hidden min-w-72 sm:block"
            ><span class="field-label">Okul kapsamı</span>
            <UiSelect
              v-model="session.selectedSchoolId"
              :options="schoolOptions"
              searchable
              search-placeholder="Okul adı veya kurum kodu ara..."
              @change="refresh"
            />
          </label>
          <button class="icon-btn" title="Yenile" @click="refresh">
            <RefreshCw :class="['size-4', refreshing && 'animate-spin']" />
          </button>
        </div>
      </header>
      <main class="p-4 lg:p-7"><slot /></main>
    </div>
    <div
      v-if="mobileOpen"
      class="fixed inset-0 z-40 bg-ink-950/50 backdrop-blur-sm lg:hidden"
      @click="mobileOpen = false"
    ></div>
  </div>
</template>
